import React, { useState, useEffect } from 'react';
import { toast } from 'sonner';
import { Helmet } from 'react-helmet-async';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { MapPin, Phone, Mail, Clock, Loader2 } from 'lucide-react';
import { submitLead, handleLeadResponse, countries, getGeoLocation } from '../utils/api';

const ContactUs = () => {
  const [loading, setLoading] = useState(false);
  const [isVisible, setIsVisible] = useState(false);
  const [selectedCountry, setSelectedCountry] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: '',
  });

  useEffect(() => {
    setIsVisible(true);
    
    // Auto-detect country
    const detectCountry = async () => {
      const geoData = await getGeoLocation();
      if (geoData?.data?.countryCode) {
        const country = countries.find(c => c.code === geoData.data.countryCode);
        if (country) {
          setSelectedCountry(country);
        } else {
          setSelectedCountry(countries.find(c => c.code === 'US'));
        }
      } else {
        setSelectedCountry(countries.find(c => c.code === 'US'));
      }
    };
    detectCountry();
  }, []);

  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handlePhoneChange = (e) => {
    const value = e.target.value.replace(/\D/g, '');
    setFormData((prev) => ({ ...prev, phone: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validation - all fields required except message
    if (!formData.name || formData.name.length < 2) {
      toast.error('Please enter a valid name');
      return;
    }
    if (!formData.email || !formData.email.includes('@')) {
      toast.error('Please enter a valid email address');
      return;
    }
    if (!formData.phone || formData.phone.length < 7) {
      toast.error('Please enter a valid phone number');
      return;
    }
    if (!formData.subject || formData.subject.length < 3) {
      toast.error('Please enter a subject');
      return;
    }
    if (!selectedCountry) {
      toast.error('Please select a country');
      return;
    }

    setLoading(true);

    try {
      const leadData = {
        name: formData.name,
        email: formData.email,
        countryDialCode: selectedCountry.dialCode,
        country: selectedCountry.code,
        phoneNumber: formData.phone,
        description: `Subject: ${formData.subject}\n\n${formData.message}`,
      };

      const response = await submitLead(leadData);

      handleLeadResponse(
        response,
        () => {
          toast.success('Thank you! Your message has been sent successfully.');
        },
        (errorMsg) => {
          toast.error(errorMsg);
          setLoading(false);
        }
      );

      if (response.success) {
        setFormData({
          name: '',
          email: '',
          phone: '',
          subject: '',
          message: '',
        });
      }
    } catch (error) {
      console.error('Error sending message:', error);
      toast.error('Failed to send message. Please try again.');
      setLoading(false);
    }
  };

  return (
    <div data-testid="contact-us-page">
      <Helmet>
        <title>Contact Us - Zorvian Associates | Get Legal Help Today</title>
        <meta name="description" content="Contact Zorvian Associates for professional legal assistance. Located in San Francisco, CA. Call us at your convenience or fill out our contact form for a free consultation." />
        <meta name="keywords" content="contact lawyer, legal consultation, law firm contact, attorney contact, San Francisco law firm, California legal services" />
      </Helmet>
      
      {/* Enhanced Hero Section */}
      <section className="relative bg-gradient-to-br from-[#0F172A] via-[#1e293b] to-[#0F172A] py-24 lg:py-32 overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0" style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='1'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`
          }}></div>
        </div>
        
        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className={`text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-6 font-heading transition-all duration-700 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
              Contact Us
            </h1>
            <p className={`text-lg text-slate-300 max-w-2xl mx-auto transition-all duration-700 delay-200 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
              Get in touch with our team. We're here to answer your questions and discuss how we can help with your legal needs.
            </p>
          </div>
        </div>
      </section>

      {/* Contact Information & Form */}
      <section className="section-padding bg-white">
        <div className="container mx-auto px-4">
          <div className="grid lg:grid-cols-5 gap-12 max-w-6xl mx-auto">
            {/* Contact Information */}
            <div className="lg:col-span-2 space-y-6">
              <div>
                <h2 className="text-3xl font-bold text-[#0F172A] mb-6 font-heading">Get In Touch</h2>
                <p className="text-slate-600 mb-8">
                  Reach out to us using any of the methods below, or fill out the contact form and we'll get back to you promptly.
                </p>
              </div>

              <Card className="border-0 shadow-md bg-slate-50">
                <CardContent className="p-6 space-y-6">
                  <div className="flex items-start space-x-4">
                    <div className="bg-blue-50 w-12 h-12 rounded-lg flex items-center justify-center flex-shrink-0">
                      <MapPin className="h-6 w-6 text-[#2563EB]" />
                    </div>
                    <div>
                      <h3 className="font-bold text-[#0F172A] mb-1">Office Address</h3>
                      <p className="text-sm text-slate-600">
                        166 Geary St Ste 1500 #1787<br />
                        San Francisco, CA 94108<br />
                        United States
                      </p>
                    </div>
                  </div>

                  <div className="flex items-start space-x-4">
                    <div className="bg-blue-50 w-12 h-12 rounded-lg flex items-center justify-center flex-shrink-0">
                      <Phone className="h-6 w-6 text-[#2563EB]" />
                    </div>
                    <div>
                      <h3 className="font-bold text-[#0F172A] mb-1">Phone</h3>
                      <p className="text-sm text-slate-600">+1 (844) 921-2228</p>
                    </div>
                  </div>

                  <div className="flex items-start space-x-4">
                    <div className="bg-blue-50 w-12 h-12 rounded-lg flex items-center justify-center flex-shrink-0">
                      <Mail className="h-6 w-6 text-[#2563EB]" />
                    </div>
                    <div>
                      <h3 className="font-bold text-[#0F172A] mb-1">Email</h3>
                      <a href="mailto:Info@zorvianassociates.com" className="text-sm text-[#2563EB] hover:underline">
                        Info@zorvianassociates.com
                      </a>
                    </div>
                  </div>

                  <div className="flex items-start space-x-4">
                    <div className="bg-blue-50 w-12 h-12 rounded-lg flex items-center justify-center flex-shrink-0">
                      <Clock className="h-6 w-6 text-[#2563EB]" />
                    </div>
                    <div>
                      <h3 className="font-bold text-[#0F172A] mb-1">Office Hours</h3>
                      <p className="text-sm text-slate-600">
                        Monday - Friday: 9:00 AM - 6:00 PM<br />
                        Saturday: 10:00 AM - 2:00 PM<br />
                        Sunday: Closed
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Contact Form */}
            <div className="lg:col-span-3">
              <Card className="border-0 shadow-lg">
                <CardContent className="p-8">
                  <h2 className="text-2xl font-bold text-[#0F172A] mb-6 font-heading">Send Us a Message</h2>
                  <form onSubmit={handleSubmit} className="space-y-6">
                    <div>
                      <Label htmlFor="name" className="text-[#0F172A] font-medium">
                        Full Name *
                      </Label>
                      <Input
                        id="name"
                        type="text"
                        value={formData.name}
                        onChange={(e) => handleChange('name', e.target.value)}
                        placeholder="John Doe"
                        className="mt-1"
                        required
                        data-testid="contact-name-input"
                      />
                    </div>

                    <div>
                      <Label htmlFor="email" className="text-[#0F172A] font-medium">
                        Email Address *
                      </Label>
                      <Input
                        id="email"
                        type="email"
                        value={formData.email}
                        onChange={(e) => handleChange('email', e.target.value)}
                        placeholder="john.doe@example.com"
                        className="mt-1"
                        required
                        data-testid="contact-email-input"
                      />
                    </div>

                    <div>
                      <Label className="text-[#0F172A] font-medium">
                        Contact Number *
                      </Label>
                      <div className="flex gap-2 mt-1">
                        <Select
                          value={selectedCountry?.code}
                          onValueChange={(value) => {
                            const country = countries.find(c => c.code === value);
                            setSelectedCountry(country);
                          }}
                        >
                          <SelectTrigger className="w-[180px]" data-testid="contact-country-select">
                            <SelectValue>
                              {selectedCountry && `+${selectedCountry.dialCode} (${selectedCountry.code})`}
                            </SelectValue>
                          </SelectTrigger>
                          <SelectContent className="max-h-[300px]">
                            {countries.map((country) => (
                              <SelectItem key={country.code} value={country.code}>
                                +{country.dialCode} - {country.name}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <Input
                          id="phone"
                          type="tel"
                          value={formData.phone}
                          onChange={handlePhoneChange}
                          placeholder="1234567890"
                          className="flex-1"
                          required
                          data-testid="contact-phone-input"
                        />
                      </div>
                      <p className="text-xs text-slate-500 mt-1">Enter numbers only</p>
                    </div>

                    <div>
                      <Label htmlFor="subject" className="text-[#0F172A] font-medium">
                        Subject *
                      </Label>
                      <Input
                        id="subject"
                        type="text"
                        value={formData.subject}
                        onChange={(e) => handleChange('subject', e.target.value)}
                        placeholder="How can we help you?"
                        className="mt-1"
                        required
                        data-testid="contact-subject-input"
                      />
                    </div>

                    <div>
                      <Label htmlFor="message" className="text-[#0F172A] font-medium">
                        Message
                      </Label>
                      <Textarea
                        id="message"
                        value={formData.message}
                        onChange={(e) => handleChange('message', e.target.value)}
                        placeholder="Please provide details about your inquiry..."
                        rows={6}
                        className="mt-1"
                        data-testid="contact-message-input"
                      />
                    </div>

                    <Button
                      type="submit"
                      className="w-full bg-[#2563EB] hover:bg-[#1d4ed8] text-white py-6 text-lg"
                      disabled={loading}
                      data-testid="contact-submit-button"
                    >
                      {loading ? (
                        <>
                          <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                          Sending...
                        </>
                      ) : (
                        'Send Message'
                      )}
                    </Button>
                  </form>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Map Section */}
      <section className="section-padding bg-slate-50">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <div className="bg-slate-200 h-96 rounded-lg flex items-center justify-center">
              <div className="text-center text-slate-500">
                <MapPin className="h-16 w-16 mx-auto mb-4" />
                <p className="text-lg font-semibold">166 Geary St Ste 1500 #1787, San Francisco, CA 94108</p>
                <p className="text-sm mt-2">Interactive map can be integrated here</p>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ContactUs;
